import React, { useEffect, useRef, useState } from 'react'

import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { batch as reduxBatch, useDispatch, useSelector } from 'react-redux'

import type { SelectChangeEvent } from '@mui/material';
import type { RootState } from 'reduxStore'

import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { getMap } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'
import { layerValidInCurrentProfile } from 'utils/profile'

import { setCurrentProfile, setLayerUnselected } from '../actions'

const styles: { [key: string]: React.CSSProperties } = {
  topMenuItem: {
    marginBottom: 10,
    paddingBottom: 10
  },
  profile: {
    margin: 16,
    marginBottom: 10,
    fontWeight: 'bold'
  }
}

export interface Profile {
  id: string
  name: string
  layers: string[]
}

export type ProfilesState = Record<string, Profile>

const Profile: React.FC = () => {
  const dispatch = useDispatch()
  const currentProfile = useSelector((state: RootState) => state.currentProfile)
  const profiles = useSelector((state: RootState) => state.profiles) as ProfilesState
  const prevProfileRef = useRef<string | null>(null)

  const [profileFromModel, setProfileFromModel] = useState<string | null>(null)

  // When the component mounts, get the initial profile from the model (i.e. from the URL params, e.g. 'profile[assets]')
  useEffect(() => {
    const initialProfile: string = Model.getState('profile', null)
    setProfileFromModel(initialProfile ? initialProfile.toLowerCase() : null)
  }, [])

  // Wait for layers to load and populate the available profiles, then update the profile, if necessary
  useEffect(() => {
    // No profile specified in the URL, or profiles have not yet loaded
    if (!profiles || Object.keys(profiles).length === 0 || profileFromModel === null) {
      return
    }

    // Profile 'all' or '*' was used in the URL, behave as if no profile was set
    if (profileFromModel === 'all' || profileFromModel === '*') {
      console.debug(`Profile '${profileFromModel}' was used in URL. Quietly ignoring and just showing all layers.`)
      updateProfile('')
      return
    }

    // Invalid profile specified in the URL
    if (!(profileFromModel in profiles)) {
      console.debug(`Profile '${profileFromModel}' is not found in any layers.  Available profiles are:`, profiles)
      PoziNotifications().warning(`Profile '${profileFromModel}' cannot be found. Showing all layers.`)
      updateProfile('')
      return
    }

    // Valid profile so update state
    updateProfile(profileFromModel)
  }, [profiles, profileFromModel])

  // Method to get all visible layers that have a profile
  const getVisibleLayersFromMap = () => {
    const map = getMap()

    const visibleLayers = map
      .getAllLayers()
      .filter(olLayer => olLayer.getVisible())
      .map(olLayer => olLayer.get('titleid') as string)

    // console.debug('Profile - getVisibleLayersFromMap - visible layers', visibleLayers)

    return visibleLayers
  }

  // Method to hide layers from map and layer tree. Catalogue layers will be igored because
  // they themselves are not shown on the map but their children are.
  const hideLayers = (layers: string[]) => {
    // console.debug('Profile - hideLayers', layers)

    // Create list of layers to hide
    const layersToHide: string[] = []

    for (const titleid of layers) {
      const layerConf = PoziUtils.getLayerFromTitleID(titleid)
      const layerIsCatalogue = layerConf && PoziUtils.isLayerTypeCatalogue(layerConf)
      if (layerIsCatalogue) {
        // console.debug('Profile - hideLayers - layer is catalogue. Ignoring.', titleid)

        continue
      }

      const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)

      if (!olLayer) {
        console.log('Profile - hideLayers - layer not found', titleid, '. Ignoring.')
        continue
      }

      const layerIsVisible = olLayer.getVisible()
      if (!layerIsVisible) {
        // console.debug('Profile - hideLayers - layer not visible', titleid)

        continue
      }

      layersToHide.push(titleid)
    }

    // console.debug('Profile - hideLayers - layers to hide', layersToHide)

    // Tell redux to unselect the layers and do it in one batch to make these calls
    // more efficient
    reduxBatch(() => {
      layersToHide.forEach(titleid => {
        // console.debug('Profile - hideLayers - hiding layer', titleid)

        dispatch(setLayerUnselected(titleid))
      })
    })
  }

  const onSelectChange = (evt: SelectChangeEvent<any>) => {
    const selectedProfile = evt.target?.value || ''
    console.debug('Profile - onSelectChange', selectedProfile, evt)
    updateProfile(selectedProfile)
  }

  const updateProfile = (profile: string) => {
    if (profile === currentProfile) {
      return
    }

    const visibleLayers = getVisibleLayersFromMap()
    const layersToHide = visibleLayers.filter((titleid: string) => !layerValidInCurrentProfile(titleid, profile))
    hideLayers(layersToHide)

    // Inform redux about the new current profile
    dispatch(setCurrentProfile(profile))

    // Inform the model about the new current profile
    Model.setState('profile', null, profile)

    // Update the URL to reflect the new current profile
    HistoryUpdate()
  }

  const hasProfiles =
    profiles &&
    Object.keys(profiles).length > 0 &&
    !Object.keys(profiles).every(id => ['*', 'all'].includes(id)) // ignore if the only available profiles are '*' or 'all' or both

  if (!hasProfiles) {
    // console.debug('Profile - render - no profiles', currentProfile, profiles)
    return null
  }

  // Make sure that the profiles appear in alphabetical order
  const profilesArrSorted = Object.values(profiles)
    .sort((a, b) => a.name < b.name ? -1 : 1) // sort alphabetically

  const menuItemArr = [
    <MenuItem style={styles.topMenuItem} key={0} value={''} divider={true}>
      All
    </MenuItem>
  ]

  // Generate menu items from profiles
  for (const profile of profilesArrSorted) {
    const { id, name } = profile

    // Ignore the wildcard profile
    if (id === '*') {
      continue
    }

    menuItemArr.push(
    <MenuItem key={id} value={id}>
      {name}
    </MenuItem>
    )
  }

  return (
    <div style={styles.profile}>
      <FormControl variant="standard" fullWidth>
        <InputLabel shrink>
          Profile
        </InputLabel>
        <Select
          value={currentProfile || ''}
          onChange={e => onSelectChange(e)}
          displayEmpty
        >
          {menuItemArr}
        </Select>
      </FormControl>
    </div>
  )
}

export default Profile